/**
 * Reports & exports.
 *
 * Everything an admin needs to take off the page and into a spreadsheet, a
 * finance system or an email. Most reports are built in the browser from data
 * the page already holds, so they match what is on screen exactly. The monthly
 * request report is the exception: it comes from the platform's own
 * /dashboard/export-report endpoint, which covers the whole history rather than
 * just what this page loaded.
 *
 * Each report is one table model (utils/billingReports) rendered twice — as the
 * preview here and as the CSV — so what an admin checks is what they download.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle, Archive, Building2, CalendarDays, CalendarRange, Check, Cloud, Download, Eye, EyeOff,
  FileSpreadsheet, Layers, ListOrdered, Loader2, TrendingUp, Users,
} from 'lucide-react'
import { dashboardApi } from '../../services/api'
import { downloadCsv, USD_GBP_RATE } from '../../utils/billing'
import {
  buildConsumptionReport, buildMonthlyReport, buildPeopleReport, buildTemplateReport, buildUsageReport,
  displayValue, encodePack, encodeReport, formatGenerated, reportFilename,
} from '../../utils/billingReports'
import { Segmented } from './BillingPrimitives'
import { isAllTimeLabel } from './useBillingData'

const LAYOUT_KEY = 'xtractai-billing-report-layout'
const PREVIEW_ROWS = 6

function readLayout() {
  try { return localStorage.getItem(LAYOUT_KEY) === 'data' ? 'data' : 'report' } catch { return 'report' }
}

function storeLayout(value) {
  try { localStorage.setItem(LAYOUT_KEY, value) } catch { /* non-fatal: the choice holds for this visit */ }
}

/** axios hands back a Blob for this endpoint (responseType: 'blob'). */
async function readJson(data) {
  if (data && typeof data.text === 'function') return JSON.parse(await data.text())
  return typeof data === 'string' ? JSON.parse(data) : data
}

const ACCENTS = {
  navy: 'bg-navy-50 text-navy-700 ring-navy-100 dark:bg-navy-900/40 dark:text-navy-300 dark:ring-navy-800',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-900',
  sky: 'bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-900/30 dark:text-sky-300 dark:ring-sky-900',
  violet: 'bg-violet-50 text-violet-700 ring-violet-100 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-900',
  amber: 'bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-900',
}

function ScopeBadge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5
                     text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {children}
    </span>
  )
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-800
                     bg-white dark:bg-gray-900 px-2 py-1 text-[11px] text-gray-600 dark:text-gray-300">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />}
      {children}
    </span>
  )
}

/** The first rows of a report's first table, formatted as the file will hold them. */
function Preview({ report, layout }) {
  const section = report.sections[0]
  if (!section) return null
  const rows = section.rows.slice(0, PREVIEW_ROWS)
  const showTotals = layout === 'report' && section.totals
  const numeric = c => c.type !== 'text' && c.type !== 'date'
  const extraSections = layout === 'report' ? report.sections.length - 1 : 0

  return (
    <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden animate-panel-in">
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-[12px]">
          <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
            <tr>
              {section.columns.map(c => (
                <th
                  key={c.key}
                  scope="col"
                  className={`px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap
                              ${numeric(c) ? 'text-right' : 'text-left'}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30">
                {section.columns.map(c => (
                  <td
                    key={c.key}
                    className={`px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-200
                                ${numeric(c) ? 'text-right tabular-nums' : 'text-left'}`}
                  >
                    {displayValue(c, row[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            {showTotals && (
              <tr className="bg-gray-50 dark:bg-gray-800/60 font-semibold">
                {section.columns.map(c => (
                  <td
                    key={c.key}
                    className={`px-3 py-1.5 whitespace-nowrap text-gray-900 dark:text-gray-100
                                ${numeric(c) ? 'text-right tabular-nums' : 'text-left'}`}
                  >
                    {section.totals[c.key] === undefined ? '' : displayValue(c, section.totals[c.key])}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="px-3 py-2 text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800">
        Showing {rows.length} of {section.rows.length} {section.rows.length === 1 ? 'row' : 'rows'}
        {extraSections > 0 && ` · the file also has ${extraSections} more ${extraSections === 1 ? 'table' : 'tables'}`}
        {showTotals && ' · totals row included'}
      </p>
    </div>
  )
}

function ReportCard({ item, layout, busy, done, previewOpen, onPreview, onDownload }) {
  const { report, status } = item
  const unavailable = status === 'error' || status === 'empty'
  const loading = status === 'loading'
  const columns = report?.sections[0]?.columns || item.columns || []
  const shownColumns = columns.slice(0, 7)

  return (
    <article
      aria-labelledby={`report-${item.key}-title`}
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4
                 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <span className={`p-2.5 rounded-lg ring-1 ring-inset flex-shrink-0 self-start ${ACCENTS[item.accent]}`}>
          <item.icon className="w-4 h-4" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 id={`report-${item.key}-title`} className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-100">
              {item.title}
            </h3>
            <ScopeBadge>{item.scopeLabel}</ScopeBadge>
          </div>
          <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{item.body}</p>

          <p className="text-[11.5px] mt-2 text-gray-600 dark:text-gray-300">
            {loading && <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Loading data…</span>}
            {status === 'error' && (
              <span className="inline-flex items-center gap-1.5 text-red-600 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5" />{item.errorText || 'This data could not be loaded.'}
              </span>
            )}
            {status === 'empty' && <span className="text-gray-400 dark:text-gray-500">{item.emptyText || 'Nothing to report for this period.'}</span>}
            {status === 'ready' && item.summary}
            {status === 'remote' && <span className="text-gray-500 dark:text-gray-400">{item.summary}</span>}
          </p>

          {columns.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2.5" aria-label="Columns in this file">
              {shownColumns.map(c => (
                <span key={c.key} className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10.5px] text-gray-600 dark:text-gray-400">
                  {c.label}
                </span>
              ))}
              {columns.length > shownColumns.length && (
                <span className="px-1.5 py-0.5 text-[10.5px] text-gray-400 dark:text-gray-500">
                  +{columns.length - shownColumns.length} more
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex sm:flex-col items-stretch gap-2 flex-shrink-0 sm:w-[132px]">
          <button
            type="button"
            onClick={() => onDownload(item)}
            disabled={busy || loading || unavailable}
            aria-label={`Download ${item.title} as CSV`}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-8 px-3 text-[12px] font-semibold
                       rounded-lg bg-navy-700 text-white hover:bg-navy-800 dark:bg-navy-600 dark:hover:bg-navy-500
                       shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-navy-500"
          >
            {busy
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : done ? <Check className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
            {busy ? 'Preparing' : done ? 'Downloaded' : 'Download CSV'}
          </button>
          <button
            type="button"
            onClick={() => onPreview(item)}
            disabled={loading || unavailable || (busy && !report)}
            aria-expanded={previewOpen}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-8 px-3 text-[12px] font-medium
                       rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200
                       hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            {previewOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {previewOpen ? 'Hide preview' : 'Preview'}
          </button>
        </div>
      </div>

      {previewOpen && report && <Preview report={report} layout={layout} />}
    </article>
  )
}

const fmtInt = n => (Number(n) || 0).toLocaleString('en-GB')
const plural = (n, one, many) => `${fmtInt(n)} ${n === 1 ? one : many}`

/**
 * @param reportKey   the period (see periodKey) every report is labelled with
 * @param loadedFor   { summary, usage, trend }: the period each source was
 *   actually loaded for. A source loaded for another period (a reload that
 *   failed leaves the old numbers on screen) is never exported under this
 *   period's name: its report reads as loading while a reload is in flight,
 *   and as unavailable once it is not. Omitted, every source is taken as current.
 * @param loadingNow  { summary, usage }: a reload of that source is in flight
 */
export default function ReportsPanel({
  summary, plan, usage, trend, range, custom, periodLabel, organisation, onError, onDone,
  reportKey = null, loadedFor = null, loadingNow = null,
}) {
  const [layout, setLayout] = useState(readLayout)
  const [busyKey, setBusyKey] = useState(null)
  const [doneKey, setDoneKey] = useState(null)
  const [previewKey, setPreviewKey] = useState(null)
  const [monthly, setMonthly] = useState(null)

  useEffect(() => {
    if (!doneKey) return undefined
    const id = setTimeout(() => setDoneKey(null), 2200)
    return () => clearTimeout(id)
  }, [doneKey])

  const onLayout = value => { setLayout(value); storeLayout(value) }

  const allTime = isAllTimeLabel(periodLabel)
  const periodText = allTime ? 'all time' : range === 'custom' ? periodLabel : `the last ${periodLabel}`
  const context = useMemo(() => ({
    organisation: organisation || null,
    period: allTime ? 'All time' : range === 'custom' ? periodLabel : `Last ${periodLabel}`,
  }), [organisation, allTime, range, periodLabel])

  const cycleConsumption = summary?.estimated_costs?.current_month?.total_estimated_usd || 0
  const todayIso = new Date().toISOString().slice(0, 10)

  const reports = useMemo(() => {
    const templateArgs = { trend: trend?.data, consumption: cycleConsumption, endIso: custom?.to || todayIso }
    return {
      usage: buildUsageReport({ usage, plan }),
      templates: trend?.data ? buildTemplateReport(templateArgs) : null,
      templatesDaily: trend?.data ? buildTemplateReport({ ...templateArgs, variant: 'daily' }) : null,
      consumption: buildConsumptionReport({ costs: summary?.estimated_costs, infrastructure: summary?.infrastructure_costs }),
      people: buildPeopleReport({
        activity: summary?.user_activity,
        groupActivity: summary?.group_activity,
        totals: summary?.user_activity_totals?.period,
      }),
    }
  }, [usage, plan, trend, cycleConsumption, custom, todayIso, summary])

  // Status of each client-built report: what the card can offer right now.
  const statusOf = report => (report && !report.empty ? 'ready' : 'empty')

  // A source is current when it was loaded for the period the reports carry.
  const isCurrent = source => !reportKey || !loadedFor || loadedFor[source] === reportKey
  const sourceStatus = (report, source) => (isCurrent(source)
    ? statusOf(report)
    : loadingNow?.[source] ? 'loading' : 'error')

  // Both template reports read the same trend data, so they load and fail together.
  const trendStatus = report => {
    if (trend?.loading && !trend?.data) return 'loading'
    if (trend?.error) return 'error'
    if (!isCurrent('trend')) return trend?.loading ? 'loading' : 'error'
    return statusOf(report)
  }
  const staleText = 'These figures could not be loaded for this period. Try Refresh in the header.'
  const trendWindow = trend?.data?.truncated
    ? `Last ${fmtInt(trend.data.dates?.length || 0)} days`
    : 'All time'

  const tplRows = reports.templates?.sections[0].rows
  const tplTotal = reports.templates?.sections[0].totals?.requests
  const dailyTemplates = reports.templatesDaily ? reports.templatesDaily.sections[0].columns.length - 3 : 0

  const items = [
    {
      key: 'templates',
      report: reports.templates,
      status: trendStatus(reports.templates),
      icon: Layers,
      accent: 'violet',
      title: 'Usage by template',
      scopeLabel: allTime ? trendWindow : 'Analysis period',
      body: `Every template that ran in ${periodText}, ranked by requests, with its share, busiest day, active days and allocated Azure cost.`,
      summary: reports.templates
        ? `${plural(tplRows?.length || 0, 'template', 'templates')} · ${fmtInt(tplTotal)} requests${tplRows?.[0] ? ` · top: ${tplRows[0].template}` : ''}`
        : null,
      emptyText: `No requests were raised against a template in ${periodText}.`,
      errorText: 'Template activity could not be loaded. Try Refresh in the header.',
    },
    {
      key: 'templatesDaily',
      report: reports.templatesDaily,
      status: trendStatus(reports.templatesDaily),
      icon: CalendarDays,
      accent: 'violet',
      title: 'Daily activity by template',
      scopeLabel: allTime ? trendWindow : 'Analysis period',
      body: `One row per day for ${periodText}, with a column per template — ready for a pivot table or a chart.`,
      summary: reports.templatesDaily
        ? `${plural(reports.templatesDaily.rowCount, 'day', 'days')} × ${plural(dailyTemplates, 'template', 'templates')} · ${fmtInt(reports.templatesDaily.sections[0].totals.total)} requests`
        : null,
      emptyText: `No requests were raised against a template in ${periodText}.`,
      errorText: 'Template activity could not be loaded. Try Refresh in the header.',
      // A day-by-template grid is too wide to sit inside the combined pack; it is a download of its own.
      inPack: false,
    },
    {
      key: 'usage',
      report: reports.usage,
      status: sourceStatus(reports.usage, 'usage'),
      icon: TrendingUp,
      accent: 'navy',
      title: 'Usage and plan position',
      scopeLabel: 'Analysis period + billing month',
      body: `Pages, documents, requests and AI tokens per ${usage?.grouping === 'weekly' ? 'week' : 'month'} for ${periodText}, followed by where this billing month stands against your plan.`,
      summary: `${plural(reports.usage.rowCount, usage?.grouping === 'weekly' ? 'week' : 'month', usage?.grouping === 'weekly' ? 'weeks' : 'months')} · ${fmtInt(reports.usage.sections[0].totals.pages)} pages · ${plan?.name || 'plan'} position`,
      emptyText: `No usage was recorded in ${periodText}.`,
      errorText: staleText,
    },
    {
      key: 'consumption',
      report: reports.consumption,
      status: sourceStatus(reports.consumption, 'summary'),
      icon: Cloud,
      accent: 'sky',
      title: 'Azure consumption breakdown',
      scopeLabel: 'Analysis period',
      body: `Every line behind the Azure estimate for ${periodText} — quantity, published rate and cost in USD and GBP — with the per-service AI split and the fixed platform cost.`,
      summary: `Estimated $${(summary?.estimated_costs?.period?.total_estimated_usd || 0).toFixed(2)} · ${plural(reports.consumption.sections.length, 'table', 'tables')}`,
      emptyText: 'No consumption estimate is available yet.',
      errorText: staleText,
    },
    {
      key: 'people',
      report: reports.people,
      status: sourceStatus(reports.people, 'summary'),
      icon: Users,
      accent: 'emerald',
      title: 'Usage by person',
      scopeLabel: 'Analysis period',
      body: `Requests and documents for ${periodText}, attributed to whoever raised them, with each person's share and a per-group table — the file behind a chargeback conversation.`,
      summary: `${plural(reports.people.rowCount, 'person', 'people')} · ${fmtInt(reports.people.sections[0].totals.requests)} requests`,
      emptyText: `Nobody raised a request in ${periodText}.`,
      errorText: staleText,
    },
    {
      key: 'monthly',
      report: monthly,
      status: monthly ? statusOf(monthly) : 'remote',
      icon: ListOrdered,
      accent: 'amber',
      title: 'Monthly request report',
      scopeLabel: 'All history',
      body: 'Generated by the platform rather than this page: every month on record, with request counts by status and a total.',
      summary: monthly
        ? `${plural(monthly.rowCount, 'month', 'months')} · ${fmtInt(monthly.sections[0].totals.total)} requests`
        : 'Fetched from the platform when you preview or download.',
      columns: [{ key: 'month', label: 'Month' }, { key: 'key', label: 'Period key' }, { key: 's', label: 'A column per status' }, { key: 'total', label: 'Total requests' }],
      emptyText: 'No requests on record yet.',
    },
  ]

  const loadMonthly = async () => {
    if (monthly) return monthly
    const res = await dashboardApi.exportReport({ format: 'json' })
    const report = buildMonthlyReport(await readJson(res.data))
    setMonthly(report)
    return report
  }

  const run = async (key, fn, label) => {
    setBusyKey(key)
    try {
      await fn()
      setDoneKey(key)
      onDone?.(`${label} downloaded`)
    } catch {
      onError?.(`Could not generate "${label}"`)
    } finally {
      setBusyKey(null)
    }
  }

  const fileContext = () => ({ ...context, generated: formatGenerated() })
  const fileName = id => reportFilename(id, { organisation, range, custom })

  const onDownload = item => run(item.key, async () => {
    const report = item.key === 'monthly' ? await loadMonthly() : item.report
    downloadCsv(fileName(report.id), encodeReport(report, fileContext(), layout))
  }, item.title)

  const onPreview = async item => {
    if (previewKey === item.key) { setPreviewKey(null); return }
    if (item.key === 'monthly' && !monthly) {
      setBusyKey('monthly')
      try {
        await loadMonthly()
      } catch {
        onError?.('Could not load the monthly request report')
        return
      } finally {
        setBusyKey(null)
      }
    }
    setPreviewKey(item.key)
  }

  const readyCount = items.filter(i => i.status === 'ready' || i.status === 'remote').length

  const onDownloadAll = () => run('all', async () => {
    const chosen = items
      .filter(i => i.status === 'ready' && i.report && i.inPack !== false)
      .map(i => i.report)
    let monthlyReport = null
    try { monthlyReport = await loadMonthly() } catch { /* the pack still goes out without it */ }
    if (monthlyReport && !monthlyReport.empty) chosen.push(monthlyReport)
    if (!chosen.length) throw new Error('Nothing to export')
    downloadCsv(fileName('report-pack'), encodePack(chosen, fileContext()))
  }, 'Full report pack')

  return (
    <div className="p-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Reports &amp; exports</h2>
          <p className="text-[11.5px] text-gray-500 dark:text-gray-400 mt-0.5 max-w-xl">
            CSV files that open cleanly in Excel, Google Sheets and Numbers. Figures match what this page shows
            for the selected period — preview any report before you download it.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented
            options={[{ value: 'report', label: 'Formatted report' }, { value: 'data', label: 'Data only' }]}
            value={layout}
            onChange={onLayout}
            ariaLabel="CSV layout"
          />
          <button
            type="button"
            onClick={onDownloadAll}
            disabled={busyKey !== null || readyCount === 0}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-[12px] font-semibold rounded-lg
                       border border-navy-200 dark:border-navy-800 text-navy-700 dark:text-navy-200
                       bg-navy-50 dark:bg-navy-900/40 hover:bg-navy-100 dark:hover:bg-navy-900/70
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            {busyKey === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : doneKey === 'all' ? <Check className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            Download all (one CSV)
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        <Chip icon={CalendarRange}>{context.period}</Chip>
        {organisation && <Chip icon={Building2}>{organisation}</Chip>}
        <Chip icon={FileSpreadsheet}>UTF-8 CSV · {layout === 'report' ? 'title block, totals and notes' : 'header row and data only'}</Chip>
        <Chip>Costs in USD and GBP (at {USD_GBP_RATE})</Chip>
      </div>

      <div data-tour="reports-list" className="mt-4 space-y-3">
        {items.map(item => (
          <ReportCard
            key={item.key}
            item={item}
            layout={layout}
            busy={busyKey === item.key}
            done={doneKey === item.key}
            previewOpen={previewKey === item.key}
            onPreview={onPreview}
            onDownload={onDownload}
          />
        ))}
      </div>

      <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-800/40 px-3.5 py-3 text-[11px] text-gray-500 dark:text-gray-400 space-y-1">
        <p>
          <strong className="font-semibold text-gray-600 dark:text-gray-300">Formatted report</strong> adds a title
          block, totals rows and notes that explain each figure. <strong className="font-semibold text-gray-600 dark:text-gray-300">Data only</strong> is
          a single header row and the rows beneath it, for importing into another system.
        </p>
        <p>
          Exported costs are the same Azure estimates shown on screen, not an invoice. Your Azure Marketplace
          billing remains the authority for what you are charged.
        </p>
      </div>
    </div>
  )
}
